import { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { useAIAssistant } from '../../hooks/useAIAssistant';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import Modal from '../ui/Modal';

interface MessageFeedbackProps {
  messageId: string;
  currentFeedback?: {
    rating: 'thumbs_up' | 'thumbs_down';
    comment?: string;
  };
}

export default function MessageFeedback({ messageId, currentFeedback }: MessageFeedbackProps) {
  const { submitFeedback } = useAIAssistant();
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [pendingRating, setPendingRating] = useState<'thumbs_up' | 'thumbs_down' | null>(null);
  const [comment, setComment] = useState('');

  const handleFeedback = (rating: 'thumbs_up' | 'thumbs_down') => {
    if (currentFeedback) {
      // Already has feedback, do nothing
      return;
    }

    if (rating === 'thumbs_down') {
      // Show comment dialog for negative feedback
      setPendingRating(rating);
      setShowCommentDialog(true);
    } else {
      // Submit positive feedback immediately
      submitFeedback(messageId, rating);
    }
  };

  const handleSubmitComment = async () => {
    if (pendingRating) {
      await submitFeedback(messageId, pendingRating, comment || undefined);
      setShowCommentDialog(false);
      setComment('');
      setPendingRating(null);
    }
  };

  const handleSkipComment = async () => {
    if (pendingRating) {
      await submitFeedback(messageId, pendingRating);
      setShowCommentDialog(false);
      setComment('');
      setPendingRating(null);
    }
  };

  const isActive = (rating: 'thumbs_up' | 'thumbs_down') => {
    return currentFeedback?.rating === rating;
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFeedback('thumbs_up')}
          disabled={!!currentFeedback}
          className={`h-7 px-2 ${
            isActive('thumbs_up')
              ? 'text-green-600 hover:text-green-700 bg-green-50'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
          title={currentFeedback ? 'Feedback already submitted' : 'Good response'}
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFeedback('thumbs_down')}
          disabled={!!currentFeedback}
          className={`h-7 px-2 ${
            isActive('thumbs_down')
              ? 'text-red-600 hover:text-red-700 bg-red-50'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
          title={currentFeedback ? 'Feedback already submitted' : 'Bad response'}
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Comment Modal */}
      <Modal
        isOpen={showCommentDialog}
        onClose={() => setShowCommentDialog(false)}
        title="Provide feedback"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={handleSkipComment}>
              Skip
            </Button>
            <Button variant="primary" onClick={handleSubmitComment}>
              Submit feedback
            </Button>
          </>
        }
      >
        <p className="text-sm text-neutral-600 mb-4">
          Help us improve by telling us what went wrong with this response. (Optional)
        </p>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="What could be better? (optional)"
          className="min-h-[100px]"
        />
      </Modal>
    </>
  );
}
